import { useRef } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { supportForwarded } from "store/actions/supportActions";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { IconDefinitionConstants } from "utils/constants";
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { TranslationKeys } from "translations";
import { DialogEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import { useSupportService } from "serviceHooks";
//#endregion
import { CompanyService } from "services";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function useMessageForwardAction(
  id: number | string,
  reloadHandler: () => void,
  isConfidential?: boolean,
  isAgencyCase?: boolean
) {
  const supportService = useSupportService();
  const dispatch = useDispatch();

  const onForwardUserSelect = (item: SelectionItem | SelectionItem[]) => {
    const selectedItem = item as SelectionItem;
    return isAgencyCase
      ? supportService.AgencyForwardSupportIssue(id as number, selectedItem.id as number)
      : supportService.ForwardSupportIssue(id as number, selectedItem.id as number);
  };

  // The byrå target list, held for as long as the picker is open so the search tab can filter it
  // instead of re-fetching it. Fetched once when the dialog opens (getForwardUserSuggestions is the
  // open moment) and re-fetched on every reopen, so it is never served stale — the caching is
  // strictly within one use of the picker.
  // Keyed on the issue id so a reused component instance never filters another issue's list.
  const agencyForwardTargets = useRef<{ id: number | string; targets: Promise<SelectionItem[]> } | null>(null);

  const loadAgencyForwardTargets = (refresh: boolean) => {
    if (refresh || agencyForwardTargets.current?.id !== id) {
      agencyForwardTargets.current = { id, targets: supportService.getAgencyForwardTargets(id as number) };
    }

    return agencyForwardTargets.current.targets;
  };

  // A stamped case forwards through AgencyForwardAsync instead of SendToAsync (Core refuses
  // SendToAsync outright for a stamped issue), to a bounded target list — the byrå's own
  // colleagues plus Wint, already ordered with Wint first — rather than the general
  // suggested-persons/company-search pair used for an ordinary ticket.
  const getForwardUserSuggestions = () => {
    return isAgencyCase
      ? loadAgencyForwardTargets(true)
      : UtilsHelper.convertToResultItem(supportService.getSuggestedSupportPersons());
  };

  // The target list is small (a byrå's own members, plus Wint) and already fetched in full for
  // the suggestions tab, so the "search" tab filters that same list client-side rather than
  // adding a second network round trip for a list this size.
  //
  // It has to reuse the fetched list to do that. Calling getAgencyForwardTargets here again fired a
  // request per keystroke — the search box types into GetAgencyForwardTargets, which re-resolves the
  // case, the byrå's members with their company relations, their names and Wint's support group on
  // every character — while the comment above claimed the opposite. Filtering the memoized promise is
  // what makes the claim true.
  const searchForwardUsers = (searchText: string) => {
    return isAgencyCase
      ? loadAgencyForwardTargets(false).then((items) =>
          items.filter((item) => item.value.toLowerCase().includes(searchText.toLowerCase()))
        )
      : UtilsHelper.convertToResultItem(CompanyService.SearchPersons(searchText));
  };

  const onForwardSuccess = () => {
    reloadHandler();
    dispatch(supportForwarded(id as number));
  };

  const selectionDialog: DialogProps = {
    isOpen: false,
    title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ForwardToColleague),
    subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ForwardToWhom),
    titleIcon: IconDefinitionConstants.WForward,
    dialogType: DialogEnums.DialogType.Selection,
    children: {
      getSuggestions: getForwardUserSuggestions,
      search: searchForwardUsers,
      handleSubmit: onForwardUserSelect,
      submitSuccessMessage: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.SupportIssue.ForwardSuccessMessage
      ),
    },
    maxWidth: "sm",
  };

  // Confidential cases warn before forwarding: the person you forward to sees the whole
  // conversation and you are removed as a participant. Confirming opens the normal picker.
  // The id is load-bearing: confirming pushes the picker onto the dialog queue, and the
  // footer then auto-closes this dialog. Without an id, closeDialog() pops the queue and
  // would remove the just-opened picker instead; with an id it closes this dialog by id.
  const showConfidentialForwardWarning = () => {
    dispatch(
      displayDialog({
        id: "confidential-forward-warning",
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ForwardConfidentialWarningTitle),
        titleIcon: IconDefinitionConstants.WInfo,
        dialogType: DialogEnums.DialogType.Message,
        type: StyleEnums.DisplayType.Info,
        children: (
          <>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ForwardConfidentialWarning)}
            <br />
            <br />
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ForwardConfidentialWarningNote)}
          </>
        ),
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Forward),
            onClick: () => {
              dispatch(displayDialog({ ...selectionDialog, onSubmit: onForwardSuccess }));
              return Promise.resolve();
            },
          },
        ],
      })
    );
    return Promise.resolve();
  };

  const forwardAction: MoreActionMenuItem = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Forward),
    icon: IconDefinitionConstants.WForward,
    ...(isConfidential
      ? { onClickHandler: showConfidentialForwardWarning }
      : { actionDialog: selectionDialog, onActionSuccess: onForwardSuccess }),
  };

  return forwardAction;
}

export default useMessageForwardAction;
